import { ILookupResult } from '../types'

interface IFundSearchCacheEntry {
  searchTerm: string;
  expires: number;
  results: ILookupResult[][] 
}

interface IFundSearchCacheOptions {
    keySuffix?: string
    keyPrefix?: string
    cacheExpiryInMinute?: number
}

export default class FundSearchCache {
    options: IFundSearchCacheOptions = {
        keySuffix: '',
        keyPrefix: '.fsc_',
        cacheExpiryInMinute: 5,
    }
    constructor(options: IFundSearchCacheOptions) {
        Object.assign(this.options, options)
    }
    
    public get(searchTerm: string) {
        return findFundSearchCacheEntry(this.makeKey(searchTerm))
    }
    
    public set(searchTerm: string, results: ILookupResult[][]) {
        cacheFundSearchResults(
            this.makeKey(searchTerm),
            searchTerm,
            results,
            this.options.cacheExpiryInMinute!)
    }
    
    public flushExpired() {
      const nowTime = new Date().getTime()
      const options = this.options
      Object.keys(localStorage).filter(s => s.startsWith(options.keyPrefix!)).forEach(s => {
        const entry = JSON.parse(localStorage.getItem(s) as string) as IFundSearchCacheEntry
        if (!entry || !entry?.expires) localStorage.removeItem(s)
        const expireTime = new Date(entry.expires).getTime()
        if (expireTime === NaN || expireTime < nowTime) localStorage.removeItem(s)
      })
    }
    
    private makeKey(searchTerm: string) {
        const { keyPrefix, keySuffix } = this.options
        const key = `${keyPrefix}_${searchTerm}_${keySuffix}`
        return key
    }
}

export function tryParseFundSearchCacheEntry(cacheEntryJson: string): IFundSearchCacheEntry | null {
  try {
    return JSON.parse(cacheEntryJson)
  } catch {
    return null
  }
}

export function findFundSearchCacheEntry(key: string): ILookupResult[][] | null {
  const cachedJson = localStorage.getItem(key)
  if (!cachedJson) return null;
  const cachedEntry = tryParseFundSearchCacheEntry(cachedJson)
  if (!cachedEntry) return null;
  
  const now = new Date()
  const expire = new Date(cachedEntry.expires)
  if (expire < now) {
    localStorage.removeItem(key)
    return null
  }
  return cachedEntry.results
}

export function cacheFundSearchResults(key: string, searchTerm: string, results: ILookupResult[][], expiryInMinute: number) {
  const expireDate = new Date()
  expireDate.setMinutes(expireDate.getMinutes() + expiryInMinute)
  const expires = expireDate.getTime()
  
  const cacheEntry: IFundSearchCacheEntry = {
    searchTerm,
    expires,
    results
  }
  
  localStorage.setItem(key, JSON.stringify(cacheEntry))
}
